import { IgAccTime, IgAccount, IgMedia } from '@core/models/ApiFields/ig-entities';
import {
    IG_ENDPOINT,
    fixdFields,
} from '@features/social-analysis/social-listening/config/api-params';
import { PageFiltersState } from '@features/social-analysis/social-shared/store/page-filters.store';
import { BaseState, DATA_STATUS } from '@features/social-analysis/social-shared/store/state.store';
import { finalize } from 'rxjs/operators';
import { HttpBuilder } from '../../social-shared/services/http/http-builder';
import {
    AccOverApiRes,
    AccOverviewData,
    AccOverviewUser,
    Data2,
    Followers,
    Interactions,
    InteractionsBreakdown,
    MediaTypesCount,
    MetricBreakdown,
    Reach,
} from '../models/I-account-overview';

export class AccOverviewHttpBuilder extends HttpBuilder<BaseState<AccOverviewData>> {
    reqData(state: BaseState<AccOverviewData>, pageFilters: PageFiltersState, igAcc: string) {
        state.isLoading.set(true);
        const url = this.buildUrl(IG_ENDPOINT);
        const params = this.#buildParams(pageFilters, igAcc);

        this.http
            .get<AccOverApiRes>(url, { params: params })
            .pipe(finalize(() => state.isLoading.set(false)))
            .subscribe({
                next: data => this.#handleUserSuccess(state, data),
                error: err => this.handleError(err?.error?.message),
            });
    }

    #buildParams(pageFilters: PageFiltersState, igAcc: string) {
        const dates = this.getDates(pageFilters.selectedDates());
        const fields = fixdFields.userAccFields.join();
        const breakdowns = fixdFields.userAccBreakdowns.join();
        return {
            ...dates,
            breakdowns,
            fields,
            mode: 1,
            'ig_account.ig_account': 'eq:' + igAcc,
            'social_media.media_type': 'neq:tag',
        };
    }

    #formatUserRes(res: AccOverApiRes) {
        const resAccData = res?.data?.[0]?.breakdown[IgAccount.prefix as 'ig_account'];
        const resAccTimeData = res?.data?.[0]?.breakdown[IgAccTime.prefix as 'ig_account_time'];

        const userAcc = new AccOverviewUser(
            resAccData?.[IgAccount.fields.igAccount],
            resAccData?.[IgAccount.fields.image],
            resAccData?.[IgAccount.fields.biography],
            resAccData?.[IgAccount.fields.followers],
            resAccData?.[IgAccount.fields.mediaCount]
        );
        const impressions = resAccTimeData?.['impressions'];
        const reach = new Reach(
            resAccTimeData?.[IgAccTime.fields.reach],
            resAccTimeData?.[IgAccTime.fields.reachAd],
            resAccTimeData?.[IgAccTime.fields.reachPost],
            resAccTimeData?.[IgAccTime.fields.reachReel],
            resAccTimeData?.[IgAccTime.fields.reachStory],
            resAccTimeData?.[IgAccTime.fields.reachFollowerPercentage],
            resAccTimeData?.[IgAccTime.fields.reachNonFollowerPercentage],
            resAccTimeData?.[IgAccTime.fields.reachUnknownPercentage]
        );
        const interactions = new Interactions(
            resAccTimeData?.[IgAccTime.fields.totalInteractions],
            resAccTimeData?.[IgAccTime.fields.totalInteractionsAd],
            resAccTimeData?.[IgAccTime.fields.totalInteractionsPost],
            resAccTimeData?.[IgAccTime.fields.totalInteractionsReel],
            resAccTimeData?.[IgAccTime.fields.totalInteractionsStory],
            resAccTimeData?.[IgAccTime.fields.accountsEngaged]
        );
        const likes = new MediaTypesCount(
            resAccTimeData?.[IgAccTime.fields.likes],
            resAccTimeData?.[IgAccTime.fields.likesAd],
            resAccTimeData?.[IgAccTime.fields.likesPost],
            resAccTimeData?.[IgAccTime.fields.likesReel],
            resAccTimeData?.[IgAccTime.fields.likesStory]
        );
        const comments = new MediaTypesCount(
            resAccTimeData?.[IgAccTime.fields.comments],
            resAccTimeData?.[IgAccTime.fields.commentsAd],
            resAccTimeData?.[IgAccTime.fields.commentsPost],
            resAccTimeData?.[IgAccTime.fields.commentsReel],
            resAccTimeData?.[IgAccTime.fields.commentsStory]
        );
        const shared = new MediaTypesCount(
            resAccTimeData?.[IgAccTime.fields.shares],
            resAccTimeData?.[IgAccTime.fields.sharesAd],
            resAccTimeData?.[IgAccTime.fields.sharesPost],
            resAccTimeData?.[IgAccTime.fields.sharesReel],
            resAccTimeData?.[IgAccTime.fields.sharesStory]
        );
        const saved = new MediaTypesCount(
            resAccTimeData?.[IgAccTime.fields.saves],
            resAccTimeData?.[IgAccTime.fields.savesAd],
            resAccTimeData?.[IgAccTime.fields.savesPost],
            resAccTimeData?.[IgAccTime.fields.savesReel],
            resAccTimeData?.[IgAccTime.fields.savesStory]
        );

        const igAccTimeValues = res?.data?.[0]?.breakdown?.[IgAccTime.prefix as 'ig_account_time'];
        const mediaBreakdown = this.#getMediaBreakdown(res?.data?.[0]?.data);

        const followersCount = new MetricBreakdown(
            igAccTimeValues?.[IgAccTime.fields.followers],
            mediaBreakdown?.['feed'][IgAccTime.fields.followers],
            mediaBreakdown?.['story'][IgAccTime.fields.followers]
        );
        const profileViews = new MetricBreakdown(
            igAccTimeValues?.[IgAccTime.fields.profileViews],
            mediaBreakdown?.['feed'][IgAccTime.fields.profileViews],
            mediaBreakdown?.['story'][IgAccTime.fields.profileViews]
        );

        const follower = new Followers(
            followersCount,
            igAccTimeValues?.[IgAccTime.fields.unfollowers],
            igAccTimeValues?.[IgAccTime.fields.followersUnknown]
        );
        const interactsBreakdown = new InteractionsBreakdown(
            profileViews,
            igAccTimeValues?.[IgAccTime.fields.profileActivity],
            igAccTimeValues?.[IgAccTime.fields.websiteClicks]
        );

        return [
            new AccOverviewData(
                userAcc,
                impressions,
                reach,
                interactions,
                likes,
                comments,
                shared,
                saved,
                follower,
                interactsBreakdown
            ),
        ];
    }

    #getMediaBreakdown(data: Data2[]) {
        const mediaBreakdown: Record<string, Record<string, number>> = {
            feed: { follower: 0, profileViews: 0 },
            story: { follower: 0, profileViews: 0 },
            reel: { follower: 0, profileViews: 0 },
        };

        data?.forEach(item => {
            const itemType = item.social_media[IgMedia.fields.mediaType];
            if (!mediaBreakdown?.[itemType]) {
                mediaBreakdown[itemType] = { follower: 0, profileViews: 0 };
            }

            mediaBreakdown[itemType][IgAccTime.fields.followers] =
                item.social_media?.[IgAccTime.fields.followers];
            mediaBreakdown[itemType][IgAccTime.fields.profileViews] =
                item.social_media?.[IgAccTime.fields.profileViews];
        });
        return mediaBreakdown;
    }

    #handleUserSuccess(state: BaseState<AccOverviewData>, data: any) {
        state.data.set(this.#formatUserRes(data));
        state.dataStatus.set(DATA_STATUS.OK);
    }
}
